import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { useDebounced } from '@/api/useDebounced';
import { useLanguage } from '@/i18n/LanguageContext';
import { SkuMeta } from '@/components/SkuMeta';
import { ActionError, newOpId, plainQty, toScaled } from '@/pages/Exceptions/shared';
import type { PutawayOverview, PutawayStockRow, PutawayTarget } from '@/api/types';
import { describeReceivingError } from './receivingModel';

const QTY_RE = /^\d+(\.\d{1,4})?$/;

/**
 * The step after receiving: goods sit in the warehouse's receiving zone and must be put away into
 * storage cells (RECEIVING -> BIN, ADR-053). It states what exists today - the handheld path
 * (Putaway), how many handheld tasks are open - and, for a role that may put away, lets the office
 * place a stock row into a REAL cell of THIS warehouse (the server lists only active, pickable
 * storage cells of that warehouse and validates the choice again). Nothing here is a second engine:
 * the office route calls the same create/execute putaway services as the handheld.
 */
export function PutawayCard({
  inboundId, warehouseName, state, canPutaway, refreshKey, onChanged,
}: {
  readonly inboundId: string;
  readonly warehouseName: string;
  readonly state: string;
  readonly canPutaway: boolean;
  /** Changes whenever the inbound page reloaded (a receipt arrived), so this block re-reads what is waiting. */
  readonly refreshKey: string;
  readonly onChanged: () => void;
}): JSX.Element | null {
  const { t } = useLanguage();
  const [bump, setBump] = useState(0);
  const result = useApi(() => endpoints.inboundPutaway(inboundId), [inboundId, refreshKey, bump]);
  const [data, setData] = useState<PutawayOverview | null>(null);
  useEffect(() => {
    // A malformed or failed answer simply leaves the block hidden: the inbound page itself keeps working.
    if (result.status === 'success' && Array.isArray(result.data?.stock) && result.data.open_tasks) setData(result.data);
  }, [result.status, result.data]);
  const [open, setOpen] = useState(false);
  const [placing, setPlacing] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  if (!data) return null;
  const waiting = data.stock.length;
  const hasTasks = data.open_tasks.count > 0;
  // Before anything was received and nothing is waiting there is nothing to say yet.
  if (waiting === 0 && !hasTasks && state !== 'RECEIVED' && state !== 'CLOSED') return null;
  const code = data.receiving?.code ?? '—';
  const key = (r: PutawayStockRow): string => `${r.location_id}:${r.sku_id}:${r.lot_id ?? ''}`;

  return (
    <section className="card next-step putaway-card" aria-labelledby="putaway-title" data-testid="putaway-card">
      <h2 id="putaway-title" className="card-title">{t('put.title')}</h2>
      {message && <p className="ok-note" role="status">{message}</p>}
      {data.receiving === null ? (
        <p className="notice" role="status" data-testid="put-no-zone">{t('put.noReceivingZone')}</p>
      ) : (
        <>
          <p className="next-step-text" data-testid="put-state">
            {waiting > 0
              ? t('put.waiting', { n: String(waiting), code, warehouse: warehouseName })
              : t('put.allDone')}
          </p>
          <div className="tsd-handoff" data-testid="put-tsd">
            <div>{t('put.tsd', { code })}</div>
            {hasTasks && <div className="meta">{t('put.tasks', { n: String(data.open_tasks.count), qty: plainQty(data.open_tasks.qty_remaining) })}</div>}
          </div>
          {waiting > 0 && canPutaway && (
            <div className="action-row">
              <button type="button" className="primary" aria-expanded={open} onClick={() => setOpen(!open)}>
                {t('put.go')}
              </button>
            </div>
          )}
          {waiting > 0 && !canPutaway && <p className="meta" role="note">{t('put.noPermission')}</p>}
          {open && canPutaway && waiting > 0 && (
            <ul className="sku-results" aria-label={t('put.listLabel')}>
              {data.stock.map((r) => (
                <li key={key(r)} data-testid="put-stock-row">
                  <div>
                    <div>{r.sku_code} — {r.sku_name}</div>
                    <SkuMeta code={r.sku_code} article={r.article} size={r.size} color={r.color} barcodes={r.barcodes} />
                    <div className="meta">{t('put.available', { n: plainQty(r.qty_available), code: r.location_code })}</div>
                    {r.is_serial_tracked && <div className="meta">{t('rcv.err.serial')}</div>}
                  </div>
                  <button
                    type="button"
                    disabled={r.is_serial_tracked}
                    aria-label={`${t('put.place')}: ${r.sku_code}`}
                    onClick={() => setPlacing(placing === key(r) ? null : key(r))}
                  >
                    {t('put.place')}
                  </button>
                  {placing === key(r) && (
                    <PlacePanel
                      inboundId={inboundId}
                      row={r}
                      onClose={() => setPlacing(null)}
                      onDone={(m) => {
                        setPlacing(null);
                        setMessage(m);
                        setBump((n) => n + 1);
                        onChanged();
                      }}
                    />
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

function PlacePanel({
  inboundId, row, onClose, onDone,
}: {
  readonly inboundId: string;
  readonly row: PutawayStockRow;
  readonly onClose: () => void;
  readonly onDone: (message: string) => void;
}): JSX.Element {
  const { t } = useLanguage();
  const uid = useId();
  const [qty, setQty] = useState(plainQty(row.qty_available));
  const [q, setQ] = useState('');
  const debounced = useDebounced(q.trim(), 250);
  const targets = useApi(() => endpoints.inboundPutawayTargets(inboundId, debounced), [inboundId, debounced]);
  const [picked, setPicked] = useState<PutawayTarget | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const opId = useRef(newOpId());
  const touch = (): void => {
    opId.current = newOpId();
  };

  async function submit(): Promise<void> {
    if (busy) return;
    let problem: string | null = null;
    if (!QTY_RE.test(qty.trim()) || toScaled(qty) <= BigInt(0)) problem = t('rcv.err.qty');
    else if (toScaled(qty) > toScaled(row.qty_available)) problem = t('put.err.qtyOver', { n: plainQty(row.qty_available) });
    else if (!picked) problem = t('put.pickCell');
    setError(problem);
    if (problem || !picked) return;
    setBusy(true);
    try {
      const res = await endpoints.inboundPutawayPlace(inboundId, {
        opId: opId.current,
        sourceLocationId: row.location_id,
        skuId: row.sku_id,
        ...(row.lot_id ? { lotId: row.lot_id } : {}),
        qty: qty.trim(),
        toLocationId: picked.id,
      });
      touch();
      onDone(t('put.done', { qty: plainQty(res.qtyPlaced), cell: res.toLocationCode }));
    } catch (err) {
      setError(describeReceivingError(t, err));
    } finally {
      setBusy(false);
    }
  }

  const list = targets.status === 'success' ? targets.data : null;
  const ids = { qty: `${uid}-qty`, find: `${uid}-find` };
  return (
    <div className="card stock-panel" role="group" aria-label={t('put.panel')} style={{ flexBasis: '100%' }}>
      <h3 className="card-title">{t('put.panel')}</h3>
      {error && <ActionError message={error} />}
      <div className="order-create">
        <div className="field-row">
          <label htmlFor={ids.qty}>{t('rcv.qty')}</label>
          <input id={ids.qty} className="qty-input" inputMode="decimal" value={qty} onChange={(e) => { setQty(e.target.value); touch(); }} />
        </div>
        <div className="sub-form">
          {picked ? (
            <p style={{ fontWeight: 600 }}>
              {t('put.selected', { code: picked.code })}{' '}
              <button type="button" className="link-button" onClick={() => { setPicked(null); touch(); }}>
                {t('rcv.change')}
              </button>
            </p>
          ) : (
            <>
              <label htmlFor={ids.find}>{t('put.search')}</label>
              <input id={ids.find} type="search" value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off" />
              {targets.status === 'error' && <p className="sub">{t('err.generic')}</p>}
              {list && list.targets.length === 0 && (
                debounced === '' ? (
                  <p className="notice" data-testid="put-no-cells">
                    {t('put.noCells')} <Link to="/warehouses">{t('nav.warehouses')}</Link>
                  </p>
                ) : (
                  <p className="sub">{t('put.noMatch')}</p>
                )
              )}
              {list && list.targets.length > 0 && (
                <ul className="sku-results" aria-label={t('put.cell')}>
                  {list.targets.map((c) => (
                    <li key={c.id} data-testid="put-target">
                      <div>
                        <div><strong>{c.code}</strong>{c.zone_name ? ` · ${t('put.cellZone', { zone: c.zone_name })}` : ''}</div>
                        <div className="meta">
                          {c.sku_count === 0 ? t('put.cellEmpty') : t('put.cellContent', { n: String(c.sku_count), qty: plainQty(c.qty_total), codes: c.sku_codes.join(', ') })}
                        </div>
                      </div>
                      <button type="button" onClick={() => { setPicked(c); touch(); }} aria-label={`${t('rcv.pick')}: ${c.code}`}>
                        {t('rcv.pick')}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {list?.truncated && <p className="meta">{t('put.truncated', { n: String(list.targets.length) })}</p>}
            </>
          )}
        </div>
        <div className="form-actions action-row">
          <button type="button" className="primary" disabled={busy} onClick={() => void submit()}>
            {t('put.submit')}
          </button>
          <button type="button" onClick={onClose} disabled={busy}>
            {t('rcv.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}
